
import React, { useState } from "react";
import { Eye, Edit, Trash, Share, ChevronDown, ChevronUp, Calendar, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuotationStatusBadge } from "./QuotationStatusBadge";
import { QuotationAmount } from "./QuotationAmount";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface MobileQuotationCardProps {
  quotation: any;
  onEdit: (quotation: any) => void;
  onDelete?: (quotation: any) => void;
  onView?: (quotation: any) => void;
  onShare?: (quotation: any) => void;
}

export function MobileQuotationCard({
  quotation,
  onEdit,
  onDelete,
  onView,
  onShare
}: MobileQuotationCardProps) {
  const [expanded, setExpanded] = useState(false);
  
  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };
  
  const formatDateTime = (dateString: string) => {
    if (!dateString) return "—";
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="bg-[#1A1E2C] dark:bg-[#1A1E2C] rounded-lg shadow mb-3 overflow-hidden border border-gray-800">
      {/* Header with code and status */}
      <div className="flex justify-between items-center p-3 bg-[#252A38] dark:bg-[#252A38]">
        <div className="font-medium text-white">
          Código: <span className="font-bold">{quotation.code}</span>
        </div>
        <QuotationStatusBadge status={quotation.status} />
      </div>

      {/* Main content */}
      <div className="p-3">
        {/* Customer name with emphasis */}
        <div className="text-xl font-bold mb-3 text-white dark:text-white">
          {quotation.customer?.name || "Cliente não especificado"}
        </div>

        {/* Amount and date */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-300 flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            Validade: {formatDate(quotation.validity_date)}
          </div>
          <div className="font-bold">
            <QuotationAmount amount={quotation.total_amount} />
          </div>
        </div>
        
        {/* Description with highlight */}
        {quotation.description && (
          <div className="mt-2 mb-3 text-sm text-white bg-[#252A38] p-3 rounded">
            <span className="font-semibold block mb-1 text-white">Descrição:</span> 
            {quotation.description}
          </div>
        )}

        {/* Payment method */}
        <div className="text-sm text-gray-300 mb-2">
          <span className="font-medium">Método de pagamento:</span> 
          <span className="ml-1">{quotation.payment_method || "—"}</span>
        </div>

        {/* Execution deadline */}
        <div className="text-sm text-gray-300">
          <span className="font-medium">Prazo de execução:</span>
          <span className="ml-1">{quotation.execution_deadline || "—"}</span>
        </div>
        
        {/* Expanded details section */}
        {expanded && (
          <div className="text-sm text-gray-300 dark:text-gray-300 mt-2 flex flex-col space-y-2 bg-[#252A38] dark:bg-[#252A38] p-3 rounded">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2 text-[#0073B7]" />
              <span className="font-medium">Criado por:</span>
              <span className="ml-2">{quotation.creator_email || quotation.created_by || "—"}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-[#0073B7]" />
              <span className="font-medium">Data e hora:</span>
              <span className="ml-2">{formatDateTime(quotation.created_at)}</span>
            </div>
            
            {/* Materials list if available */}
            {quotation.materials && quotation.materials.length > 0 && (
              <div className="mt-2">
                <h4 className="font-medium mb-2 text-white">Materiais:</h4>
                <div className="space-y-2">
                  {quotation.materials.map((material: any, index: number) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{material.name} ({material.quantity}x)</span>
                      <span>R$ {material.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expand/collapse button */}
      <div className="flex border-t border-gray-800">
        <Button
          variant="ghost"
          className="flex-1 rounded-none py-2 text-sm text-gray-300 dark:text-gray-300 hover:bg-[#252A38] hover:dark:bg-[#252A38]"
          onClick={toggleExpand}
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" /> Menos
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" /> Mais
            </>
          )}
        </Button>
      </div>

      {/* Actions */}
      <div className="flex border-t border-gray-800 dark:border-gray-800">
        {onView && (
          <>
            <Button
              variant="ghost"
              className="flex-1 rounded-none py-2 text-sm text-[#0073B7] hover:bg-[#252A38]"
              onClick={() => onView(quotation)}
            >
              <Eye className="h-4 w-4 mr-1" /> Visualizar
            </Button>
            <div className="w-px bg-gray-800" />
          </>
        )}
        
        <Button
          variant="ghost"
          className="flex-1 rounded-none py-2 text-sm text-[#0073B7] hover:bg-[#252A38]"
          onClick={() => onEdit(quotation)}
        >
          <Edit className="h-4 w-4 mr-1" /> Editar
        </Button>
        
        {onShare && (
          <>
            <div className="w-px bg-gray-800" />
            <Button
              variant="ghost"
              className="flex-1 rounded-none py-2 text-sm text-[#0073B7] hover:bg-[#252A38]"
              onClick={() => onShare(quotation)}
            >
              <Share className="h-4 w-4 mr-1" /> Compartilhar
            </Button>
          </>
        )}
        
        {onDelete && (
          <>
            <div className="w-px bg-gray-800" />
            <Button
              variant="ghost"
              className="flex-1 rounded-none py-2 text-sm text-[#0073B7] hover:bg-[#252A38]"
              onClick={() => onDelete(quotation)}
            >
              <Trash className="h-4 w-4 mr-1" /> Excluir
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
